/**
 * VillageStory — the village told in the DOM (#583/#584 N1 slice S8; design D1, D2, D9).
 *
 * The illustrated view is complete on its own. This section holds every house, every milestone the
 * server recorded (with its earned date, or "Recorded when village history began", and any "record
 * corrected" detail), the projects the family replaced, and everyone who helped, in name order and
 * with no amount. Nothing here reads a percentage to decide what was built: a milestone is an award
 * row or it is not shown (D2).
 *
 * EVERY SCENE TARGET HAS A DOM TWIN. Each house and milestone is a list button carrying
 * `data-scene-target`, and pressing it opens the detail sheet. Escape or Close shuts the sheet and
 * returns focus to whatever opened it; Tab stays inside while it is open.
 *
 * THE SEAM FOR S9. `renderScene({ targets, openTarget })` is how the lazily loaded 3D scene joins in:
 * it gets the same targets, in list order, and calls `openTarget(id)` on a selection, which opens the
 * SAME sheet the list button opens. Its own "Explore the scene" control and the remembered "Use
 * illustrated view" choice live with it, and its canvas stays `aria-hidden`. Nothing passes it today,
 * so nothing but the list is offered, and reduced motion or a missing graphics device changes nothing.
 * No quest, approval, money or reward is reachable from here: the sheet only reads.
 *
 * WHILE THE SHEET IS OPEN, the page behind it is `inert` and does not scroll, so a screen reader's
 * virtual cursor and a touch scroll both stay on the sheet (review r1 L6). Focus returns to the
 * opener only after the page is released, because a browser will not focus an inert element.
 *
 * DATES ARE THE HOUSEHOLD'S. `timeZone` is the family's zone (`GET /api/tenants/current`). Without
 * one, a date is written in UTC and says so: the reader's zone would put a milestone on the wrong
 * day for a travelling parent or a tablet set to another zone (review r1 M1).
 *
 * TROPHIES is linked only when the host passes `trophiesHref`. `/trophies` is still the fixture
 * gallery on this branch, so a default link would send a family from its real story to a sample
 * family's invented badges (review r1 M4). The host passes it once that route is live.
 */
import React, { useCallback, useEffect, useId, useInsertionEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const STYLE_ID = 'village-story-styles';
export const STYLES = `
.vs { margin-top: var(--s-8, 32px); color: var(--text-hi, #f4f1e8); }
.vs h2, .vs h3, .vs h4 { font-family: var(--font-display, 'Fraunces', Georgia, serif); font-weight: 560; margin: 0; }
.vs h2 { font-size: var(--fs-lg, 1.5rem); color: var(--text-mid, #c0c5dc); }
.vs h3 { font-size: var(--fs-base, 1rem); margin-top: var(--s-6, 24px); }
.vs h4 { font-size: var(--fs-sm, .9rem); }
.vs p { margin: var(--s-2, 8px) 0 0; font-size: var(--fs-sm, .9rem); color: var(--text-mid, #c0c5dc); }
.vs-note { padding: var(--s-3, 12px) var(--s-4, 16px); border-radius: var(--r-md, 16px); background: var(--surface-2, #1b2342); border: 1px solid var(--line, rgba(160,170,200,.22)); }
.vs-project { margin-top: var(--s-4, 16px); padding: var(--s-4, 16px); border-radius: var(--r-lg, 28px); background: var(--surface-1, #121831); border: 1px solid var(--line, rgba(139,147,184,.22)); }
.vs-list { display: grid; gap: var(--s-2, 8px); grid-template-columns: 1fr; list-style: none; margin: var(--s-3, 12px) 0 0; padding: 0; }
@media (min-width: 720px) { .vs-list--houses { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.vs-people { display: flex; flex-wrap: wrap; gap: var(--s-2, 8px); list-style: none; margin: var(--s-2, 8px) 0 0; padding: 0; }
.vs-people li { padding: 4px 12px; border-radius: 999px; background: var(--surface-2, #1b2342); font-size: var(--fs-sm, .9rem); }
.vs-target {
  display: block; width: 100%; min-height: 44px; padding: var(--s-2, 8px) var(--s-4, 16px); text-align: left;
  font: 500 var(--fs-sm, .9rem) var(--font-ui, sans-serif); color: var(--text-hi, #f4f1e8); cursor: pointer;
  border-radius: var(--r-md, 16px); border: 1px solid var(--line, rgba(160,170,200,.22)); background: var(--surface-2, #1b2342);
}
.vs-target:focus-visible, .vs-close:focus-visible, .vs a:focus-visible { outline: 3px solid var(--accent, #efc24b); outline-offset: 2px; }
.vs a { color: var(--accent-text, #805300); display: inline-block; min-height: 44px; padding: 12px 0; }
.vs-backdrop { position: fixed; inset: 0; z-index: 60; background: rgba(4, 6, 16, .6); }
.vs-sheet {
  position: fixed; top: 0; right: 0; bottom: 0; z-index: 61; width: min(420px, 100%); height: 100%; overflow-y: auto; overscroll-behavior: contain;
  padding: var(--s-6, 24px); background: var(--surface-1, #121831); color: var(--text-hi, #f4f1e8);
  border-left: 1px solid var(--line, rgba(139,147,184,.22)); box-shadow: var(--shadow-2, 0 12px 40px rgba(2,4,12,.45));
}
.vs-sheet-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--s-3, 12px); }
.vs-close {
  flex: none; min-height: 44px; min-width: 44px; padding: 0 var(--s-4, 16px); cursor: pointer;
  font: 600 var(--fs-sm, .9rem) var(--font-ui, sans-serif); color: var(--text-hi, #f4f1e8);
  border-radius: 999px; border: 1px solid var(--line, rgba(160,170,200,.22)); background: var(--surface-2, #1b2342);
}
@media (max-width: 480px) {
  .vs-sheet { left: 0; width: 100%; height: 100%; border-left: 0; border-radius: 0; }
}
@media (forced-colors: active) { .vs-target, .vs-close, .vs-sheet { border: 1px solid CanvasText; } }
`;

function useStyles() {
  useInsertionEffect(() => {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);
}

const DAY = { year: 'numeric', month: 'long', day: 'numeric' };
const DAY_AND_TIME = { ...DAY, hour: 'numeric', minute: '2-digit' };

/**
 * A formatter for instants in the HOUSEHOLD's zone, the shape of `heldMoney.formatWhen`: an unknown
 * zone counts as no zone. With no zone the instant is written in UTC and labelled "(UTC)", which is
 * true wherever it is read. `DashboardRoute.makeTimeFormatter` is not reused: it prints a time of
 * day only, and it falls back to the reader's zone, which is the guess this avoids.
 */
export function makeDateFormatter(timeZone, parts = DAY) {
  let zoned = null;
  if (typeof timeZone === 'string' && timeZone) {
    try { zoned = new Intl.DateTimeFormat('en-US', { ...parts, timeZone }); } catch { zoned = null; }
  }
  const utc = new Intl.DateTimeFormat('en-US', { ...parts, timeZone: 'UTC' });
  return (value) => {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return 'an unrecorded date';
    return zoned ? zoned.format(d) : `${utc.format(d)} (UTC)`;
  };
}

function milestoneWhen(m, formatDate) {
  return m.recordedAtHistoryStart
    ? `Recorded when village history began, ${formatDate(m.earnedAt)}`
    : `Earned ${formatDate(m.earnedAt)}`;
}

function joinTitles(titles) {
  return titles.length < 2 ? titles.join('') : `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
}

/** What the sheet says for one target. Read-only text: no control here changes anything. */
function detailFor(target, story, formatDate) {
  if (target.kind === 'milestone') {
    const lines = [
      `${milestoneWhen(target, formatDate)}.`,
      `Milestone threshold: ${target.threshold} points.`,
    ];
    if (target.correction) lines.push(`${target.correction.text} (${formatDate(target.correction.at)})`);
    lines.push('Milestones belong to the whole family, and they stay when a new project starts.');
    return { title: `${target.label} — ${target.projectTitle}`, lines };
  }
  const projects = [story.current, ...story.earlier].filter(Boolean);
  const helped = projects.filter((p) => p.contributors.some((c) => c.userId === target.userId)).map((p) => p.title);
  return {
    title: `${target.name}’s house`,
    lines: [
      helped.length ? `${target.name} helped with ${joinTitles(helped)}.` : `No work by ${target.name} is recorded on the projects listed here.`,
      'Every house belongs to the village. The village does not keep score between people.',
    ],
  };
}

/**
 * Everything outside `layer` goes `inert` and the page stops scrolling, until the sheet unmounts.
 * Each ancestor's other children are marked, up to <body>; an element that was already inert is left
 * alone and stays inert afterwards. A layout effect, so its cleanup has run before the opener is
 * focused again.
 * Newly inserted background siblings are isolated too. Presence may refresh while this is open;
 * its polite summary stays in the inert background and is not an interruption of the detail.
 */
function useModalLayer(layerRef) {
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer || typeof document === 'undefined') return undefined;
    const marked = [];
    const isolate = () => {
      for (let node = layer; node.parentElement && node !== document.body; node = node.parentElement) {
        for (const sibling of node.parentElement.children) {
          if (sibling === node || sibling.hasAttribute('inert')) continue;
          sibling.setAttribute('inert', '');
          marked.push(sibling);
        }
      }
    };
    isolate();
    const observer = new MutationObserver(isolate);
    observer.observe(document.body, { childList: true, subtree: true });
    const { body } = document;
    const overflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      observer.disconnect();
      for (const el of marked) el.removeAttribute('inert');
      body.style.overflow = overflow;
    };
  }, [layerRef]);
}

function DetailSheet({ detail, onClose, trophiesHref }) {
  const titleId = useId();
  const layerRef = useRef(null);
  const sheetRef = useRef(null);
  const closeRef = useRef(null);
  useModalLayer(layerRef);

  useEffect(() => { if (closeRef.current) closeRef.current.focus(); }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab' || !sheetRef.current) return;
      const focusables = [...sheetRef.current.querySelectorAll('a[href], button')];
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const inside = sheetRef.current.contains(document.activeElement);
      if (!inside || (!event.shiftKey && document.activeElement === last)) { event.preventDefault(); first.focus(); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // One layer holds the backdrop and the sheet, so the backdrop is not made inert and a tap on it
  // still closes the sheet.
  return (
    <div className="vs-layer" ref={layerRef}>
      <div className="vs-backdrop" aria-hidden="true" onClick={onClose} />
      <div className="vs-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={sheetRef} data-part="detail">
        <div className="vs-sheet-head">
          <h3 id={titleId}>{detail.title}</h3>
          <button type="button" className="vs-close" ref={closeRef} onClick={onClose}>Close</button>
        </div>
        {detail.lines.map((line) => <p key={line}>{line}</p>)}
        {trophiesHref ? <p><a href={trophiesHref}>Personal badges live in Trophies</a></p> : null}
      </div>
    </div>
  );
}

function ProjectStory({ project, level, onOpen, formatDate }) {
  const titleId = useId();
  const Heading = `h${level}`;
  const dates = project.endedAt
    ? `Started ${formatDate(project.startedAt)} · closed ${formatDate(project.endedAt)}`
    : `Started ${formatDate(project.startedAt)}`;
  return (
    <article className="vs-project" aria-labelledby={titleId}>
      <Heading id={titleId}>{project.title}</Heading>
      <p>
        {project.pointTarget > 0 ? `${project.total} of ${project.pointTarget} points` : `${project.total} points`} · {dates}
      </p>
      {project.historyBeganAt ? (
        <p>Recorded when village history began, {formatDate(project.historyBeganAt)}. Work from before then counts in the total.</p>
      ) : null}
      {project.milestones.length ? (
        <ul className="vs-list" aria-label={`Milestones of ${project.title}`}>
          {project.milestones.map((m) => (
            <li key={m.id}>
              <button type="button" className="vs-target" data-scene-target={m.id} onClick={(e) => onOpen(m.id, e.currentTarget)}>
                {m.label} · {milestoneWhen(m, formatDate)}{m.correction ? ' · record corrected' : ''}
              </button>
            </li>
          ))}
        </ul>
      ) : <p>No milestones recorded yet.</p>}
      {project.contributors.length ? (
        <>
          <p>Everyone who helped, in name order:</p>
          <ul className="vs-people" aria-label={`Everyone who helped with ${project.title}`}>
            {project.contributors.map((c) => (
              <li key={c.userId}>{c.current ? c.name : `${c.name} (not currently active)`}</li>
            ))}
          </ul>
        </>
      ) : <p>Nobody’s work is recorded on this project yet.</p>}
    </article>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.story                 `toVillageStory(...)` with `enabled: true`.
 * @param {Function} [props.renderScene]       S9's seam: `({ targets, openTarget }) => node`.
 * @param {string} [props.timeZone]            The household's IANA zone; without one, dates say "(UTC)".
 * @param {string} [props.trophiesHref]        Linked only when passed: pass it once `/trophies` is live.
 */
export default function VillageStory({ story, renderScene, timeZone, trophiesHref = null }) {
  useStyles();
  const headingId = useId();
  const earlierId = useId();
  const [selectedId, setSelectedId] = useState(null);
  const openerRef = useRef(null);
  const returnFocusRef = useRef(false);
  const formatDate = useMemo(() => makeDateFormatter(timeZone), [timeZone]);
  const formatAsOf = useMemo(() => makeDateFormatter(timeZone, DAY_AND_TIME), [timeZone]);

  const selected = story.targets.find((target) => target.id === selectedId);
  const detail = selected ? detailFor(selected, story, formatDate) : null;

  const openTarget = useCallback((id, opener) => {
    const target = story.targets.find((t) => t.id === id);
    if (!target) return;
    openerRef.current = opener || (typeof document !== 'undefined' ? document.activeElement : null);
    setSelectedId(id);
  }, [story]);

  const close = useCallback(() => {
    returnFocusRef.current = true;
    setSelectedId(null);
  }, []);

  // After the sheet has unmounted, and so after its layer released the page, focus the opener.
  useEffect(() => {
    if (detail || !returnFocusRef.current) return;
    returnFocusRef.current = false;
    const opener = openerRef.current;
    if (opener && typeof opener.focus === 'function') opener.focus();
  }, [detail]);

  return (
    <section className="vs" data-component="VillageStory" aria-labelledby={headingId}>
      <h2 id={headingId}>What your village keeps</h2>
      <p>Milestones stay with the project that earned them, even after your family starts a new one.</p>
      {story.asOf ? (
        <p data-part="story-as-of">The story as of <time dateTime={story.asOf}>{formatAsOf(story.asOf)}</time>.</p>
      ) : null}
      {story.awardsPending ? (
        <p className="vs-note">New milestones are still being written down. They will appear here soon.</p>
      ) : null}
      {typeof renderScene === 'function' ? renderScene({ targets: story.targets, openTarget }) : null}

      {story.current ? <ProjectStory project={story.current} level={3} onOpen={openTarget} formatDate={formatDate} /> : null}
      {!story.current && story.unrecorded ? (
        <p className="vs-note">
          {story.unrecorded.title}: this project’s story begins with its first recorded quest.
        </p>
      ) : null}

      {story.houses.length ? <>
      <h3>Houses</h3>
      <ul className="vs-list vs-list--houses" aria-label="Houses in the village">
        {story.houses.map((h) => (
          <li key={h.id}>
            <button type="button" className="vs-target" data-scene-target={h.id} onClick={(e) => openTarget(h.id, e.currentTarget)}>
              {h.name}’s house
            </button>
          </li>
        ))}
      </ul>

      </> : null}

      {story.earlier.length ? (
        <section aria-labelledby={earlierId}>
          <h3 id={earlierId}>Earlier projects</h3>
          {story.earlier.map((p) => <ProjectStory key={p.projectId} project={p} level={4} onOpen={openTarget} formatDate={formatDate} />)}
          {story.moreEarlier ? <p>Older projects are kept too. Only the latest ones are listed here.</p> : null}
        </section>
      ) : null}

      {trophiesHref ? <p><a href={trophiesHref}>See the badges each person has earned in Trophies</a></p> : null}

      {detail ? <DetailSheet detail={detail} onClose={close} trophiesHref={trophiesHref} /> : null}
    </section>
  );
}
